import {ChangeDetectionStrategy, Component, computed, effect, input, output, signal, Signal, WritableSignal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {toSignal} from "@angular/core/rxjs-interop";
import {GruppeId, ManglerBesvarelsePåSpørreundersøkelseModell, RespondentStatus, SpørreundersøkelseEnkelModell, SpørreundersøkelseId, SpørreundersøkelseRespondentModell, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {AvatarKomponent} from "web-ng2/delt/komponenter/avatar/avatar";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {TranslateModule} from "@ngx-translate/core";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {KlikkMedStopPropagationDirektiv} from "web-ng2/delt/direktiver/klikkMedStopPropagation/klikkMedStopPropagation";
import {map} from "rxjs/operators";
import {fadeInnAnimasjon} from "web-ng2/delt/animasjoner";
import SpørreundersøkelseBesvarelseDialogKomponent from "web-ng2/moduler/spørreundersøkelse/modale/spørreundersøkelse-besvarelse-dialog/spørreundersøkelse-besvarelse-dialog";
import {MatDialog} from "@angular/material/dialog";
import {firstValueFrom} from "rxjs";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {nil} from "felles-ng2/typer/nullish";

const debug = initDebugger('SpørreundersøkelserPopupKomponent');

/** Panel som viser oversikt over spørreundersøkelser som skulle vært besvart */
@Component({
    selector: 'spoerreundersoekelser-popup',
    templateUrl: 'spørreundersøkelser-popup.html',
    styleUrl: 'spørreundersøkelser-popup.scss',
    animations: [fadeInnAnimasjon('100ms')],
    imports: [
        AvatarKomponent,
        MatIcon,
        MatIconButton,
        TranslateModule,
        KlikkMedStopPropagationDirektiv,
        ZonedDateTimeSomPlainDatePipe,
        FormaterPlainDatePipe,
        MatButton
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreundersøkelserPopupKomponent {

    vedLukk = output<boolean>();

    ubesvarteUndersoekelser = input.required<ManglerBesvarelsePåSpørreundersøkelseModell[]>();
    maksPopupHoeyde = input<number>(100);

    ubesvartAvBruker = computed(() => this.ubesvarteUndersoekelser()
      .filter(ubesvart => erIkkeNil(this.finnKallendeBruker(ubesvart)))
      .map(ubesvart => this.somUndersøkelseOgResponsStatus(ubesvart)));
    ubesvartAvAndre = computed(() => this.ubesvarteUndersoekelser());
    harSkrivetilgang = toSignal(this.kontekst.autentisertBrukerSomStrøm().pipe(map(bruker => bruker.inneharTilgangsrolleINoenGruppe(TilgangsrolleMedlem.skrive))), {initialValue: false});
    readonly statuser = RespondentStatus;

    private respondenterVises = new Map<string, WritableSignal<boolean>>();

    constructor(private navigasjon: Navigasjon, private kontekst: BrukerkontekstTilbyder, private oversettelser: Oversettelser, private dialog: MatDialog) {
        //sørg for at alle undersøkelser har et innslag i respondenterVises
        effect(() => this.ubesvarteUndersoekelser()
          .map(ubesvart => ubesvart.undersoekelse.id)
          .filter(id => !this.respondenterVises.has(id))
          .forEach(id => this.respondenterVises.set(id, signal(false))));
    }

    antallSomHarBesvart(ubesvart: ManglerBesvarelsePåSpørreundersøkelseModell): number {
        return ubesvart.respondenter.filter(respondent => respondent.status != RespondentStatus.ikke_besvart).length;
    }

    visEllerSkjulRespondenter(id: SpørreundersøkelseId) {
        this.respondenterVises.get(id)?.update(vises => !vises);
    }

    viserRespondenterSignal(id: SpørreundersøkelseId): Signal<boolean> {
        return this.respondenterVises.get(id) ?? signal(false); //bruker ?? i tilfelle angitt id mot formodning ikke skulle være lagt til i map
    }

    gaaTilUndersoekelse(undersøkelse: SpørreundersøkelseEnkelModell) {
        this.navigasjon.gåTilGruppeStatistikk(undersøkelse.eiendeGruppeId);
    }

    async besvarUndersoekelse(undersøkelse: SpørreundersøkelseEnkelModell) {
        const dialogRef = SpørreundersøkelseBesvarelseDialogKomponent.vis(this.dialog, undersøkelse.id, undersøkelse.kallendeBrukersRespondentId);
        const harBesvart = await firstValueFrom(dialogRef.beforeClosed(), {defaultValue: false});
        this.lukk(harBesvart);
    }

    gruppeinfo(gruppeId: GruppeId): string {
        const gruppe = this.kontekst.autentisertBruker().finnAutentiserteGruppe(gruppeId);
        return erIkkeNil(gruppe) ?
          this.oversettelser.oversett(i18n('i {tittel} i {organisasjonsnavn}'), {tittel: gruppe.tittel, organisasjonsnavn: gruppe.organisasjonsnavn}) :
          '';
    }

    lukk(relastData = false) {
        this.vedLukk.emit(relastData);
    }

    //private hjelpemetoder

    private finnKallendeBruker(modell: ManglerBesvarelsePåSpørreundersøkelseModell): SpørreundersøkelseRespondentModell|nil {
        return modell.respondenter.find(respondent => respondent.medlem.brukerId == this.kontekst.autentisertBruker().bruker.id);
    }

    private somUndersøkelseOgResponsStatus(modell: ManglerBesvarelsePåSpørreundersøkelseModell):UndersøkelseOgResponsStatus{
        return {undersoekelse: modell.undersoekelse, status: this.finnKallendeBruker(modell)!.status};
    }
}

type UndersøkelseOgResponsStatus = {
    undersoekelse: SpørreundersøkelseEnkelModell;
    status: RespondentStatus;
}
